/**
 * Bot Score Encryption
 * AES-GCM encryption for secure transport of bot detection results
 */

import type { BotDetectionResult } from './types';

/**
 * Get the encryption key from environment
 */
function getSecretKey(): string {
  const secret = import.meta.env.VITE_BOT_SCORE_SECRET;
  if (!secret) {
    console.warn('[BotDetection] VITE_BOT_SCORE_SECRET not configured, using fallback');
    // Fallback for development - in production, this should be configured
    return '0000000000000000000000000000000000000000000000000000000000000000';
  }
  return secret;
}

/**
 * Convert hex string to Uint8Array
 */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Convert Uint8Array to hex string
 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Import the secret key for AES-GCM encryption
 */
async function importKey(secretHex: string): Promise<CryptoKey> {
  const keyBytes = hexToBytes(secretHex);

  return crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
}

/**
 * Encrypt bot detection result using AES-GCM
 *
 * Format: base64(nonce || ciphertext || authTag)
 * - nonce: 12 bytes (96 bits) - standard for AES-GCM
 * - ciphertext: variable length
 * - authTag: 16 bytes (128 bits) - included in ciphertext by WebCrypto
 */
export async function encryptBotScore(result: BotDetectionResult): Promise<string> {
  try {
    const secretHex = getSecretKey();
    const key = await importKey(secretHex);

    // Generate random nonce (12 bytes for AES-GCM)
    const nonce = crypto.getRandomValues(new Uint8Array(12));

    // Prepare payload
    const payload = {
      score: result.score,
      signals: result.signals,
      fingerprint: result.fingerprint,
      timestamp: result.timestamp,
      nonce: result.nonce,
    };

    const encoder = new TextEncoder();
    const plaintext = encoder.encode(JSON.stringify(payload));

    // Encrypt with AES-GCM
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        tagLength: 128, // 16 bytes auth tag
      },
      key,
      plaintext
    );

    // Combine nonce + ciphertext (auth tag is already appended by WebCrypto)
    const combined = new Uint8Array(nonce.length + ciphertext.byteLength);
    combined.set(nonce, 0);
    combined.set(new Uint8Array(ciphertext), nonce.length);

    // Return as base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('[BotDetection] Encryption error:', error);
    // Return a placeholder that server will reject
    return '';
  }
}

/**
 * Create an encrypted bot score header value
 */
export async function createBotScoreHeader(result: BotDetectionResult): Promise<string> {
  return encryptBotScore(result);
}
